import { useState } from 'react'
import { APPROVAL_WINDOW_DAYS, isRole, roleHasCapability } from '@qim/shared'
import { ApiError, setProtection } from '../api.js'
import { Modal } from './modal.jsx'
import type { TenantKind } from '@qim/shared'
import type { JSX } from 'react'

/**
 * Whether an instance is protected, drawn as a lock beside its name (IM-174).
 *
 * Protection (IM-39) was a sentence and a button on the instance's own screen
 * and a chip in the status column of two lists — so the one fact that changes
 * what Destroy will do was absent from the palette, the breadcrumb, the account
 * scan and the approval queue, which are exactly the places somebody reaches an
 * instance from on the way to tearing it down. The name is the thing every one
 * of those places shows, so the mark goes beside the name.
 *
 * ## Only in Qrvey's own customers
 *
 * Protection means something only under the `protected-destroys` policy: a
 * customer's destroys already wait for the customer, and the API refuses to set
 * a mark in their tenant. An unlocked lock on a customer's instance would read
 * as "this could be protected and is not", which is false, so nothing is drawn
 * there at all — not even the muted one.
 *
 * ## Why lists show only the locked one
 *
 * Most instances are not protected. An unlocked lock on every row is a column of
 * noise whose one interesting value — the locked row — is then the hardest thing
 * in it to see; absence reads cleanly as "nothing special here" in a list whose
 * rows already say which customer they belong to. The instance's own screen is
 * the exception, and draws both: there the question is being asked directly,
 * and "not protected" is the answer, not the absence of one.
 *
 * ## Never the shape alone
 *
 * A word plus a shape, colour third, as everywhere in the console: the lock
 * carries its state as an accessible name and a tooltip in lists, and as a
 * visible word on the instance screen. Inline SVG with `currentColor`, for the
 * reason `modal.tsx` gives — the CSP is `font-src 'self'`, and an icon font
 * would be a dependency for two glyphs.
 */

/** The mark as every list carries it: who set it is the tooltip's detail. */
export interface LockMark {
  readonly by: string
}

/** The glyph, and nothing else — whatever holds it carries the words. */
export const LockIcon = ({ locked }: { readonly locked: boolean }): JSX.Element => (
  <svg
    className="icon lock-icon"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="3" y="7" width="10" height="7" rx="1.5" />
    {/* The open shackle swings clear of the body on one side, which is the
      difference a glance has to catch at 16px. */}
    {locked ? (
      <path d="M5.25 7V5a2.75 2.75 0 0 1 5.5 0v2" />
    ) : (
      <path d="M5.25 7V5a2.75 2.75 0 0 1 5.4-.75" />
    )}
    <path d="M8 10v1.5" />
  </svg>
)

const tooltipFor = (mark: LockMark): string =>
  `Protected by ${mark.by} — a destroy needs a second approver`

/**
 * The read-only lock for a list: drawn only for a protected instance in one of
 * Qrvey's own customers, and nothing otherwise — see the note above.
 *
 * `kind` absent (the tenant list still arriving) draws nothing, the same answer
 * the instance screen gives while it cannot tell.
 */
export const ProtectionLock = ({
  mark,
  kind,
}: {
  readonly mark: LockMark | undefined
  readonly kind: TenantKind | undefined
}): JSX.Element | null =>
  kind !== 'qrvey' || mark === undefined ? null : (
    // The space before it belongs to the lock, so a row with no lock ends at
    // its name rather than in whitespace a copy-paste would carry.
    <>
      {' '}
      <span className="lock lock-on" role="img" aria-label="Protected" title={tooltipFor(mark)}>
        <LockIcon locked />
      </span>
    </>
  )

/** The sentence a refusal came back with, or a plain one if it had none. */
const refusalOf = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as { detail?: unknown; error?: unknown } | undefined
    if (typeof body?.detail === 'string') return body.detail
    if (typeof body?.error === 'string') return body.error
    if (error.status === 403) return 'Your role does not permit that.'
  }
  return 'Could not save that just now.'
}

/**
 * The lock on an instance's own screen, which is also the control (IM-174).
 *
 * Offered by capability, as the toggle it replaces was: protecting is
 * `records:manage`, removing is `deployment:approve` — so `internal-ops`, which
 * holds destroy, can lock an instance and never unlock it, which is the point of
 * IM-39. Not a control in the security sense; the API checks both, and a role
 * that may not change it sees the same lock with its state in words.
 *
 * Locking is one click, because it only ever makes a destroy harder. Unlocking
 * asks first, and says the thing the mark's removal does not look like it does:
 * the person who removes it is still treated as protected against for one
 * approval window (`isProtectedFor`), or four eyes would last one click.
 */
export const ProtectionControl = ({
  baseUrl,
  tenantId,
  deployment,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly deployment: {
    readonly deploymentId: string
    readonly accountKey: string
    readonly protected?: LockMark | undefined
  }
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const mark = deployment.protected
  const locked = mark !== undefined
  const may =
    isRole(role) && roleHasCapability(role, locked ? 'deployment:approve' : 'records:manage')
  const word = locked ? 'Protected' : 'Not protected'

  const change = async (protect: boolean): Promise<void> => {
    setBusy(true)
    setProblem(undefined)
    try {
      await setProtection(
        baseUrl,
        tenantId,
        deployment.accountKey,
        deployment.deploymentId,
        protect,
      )
      onChanged()
    } catch (error: unknown) {
      setProblem(refusalOf(error))
    } finally {
      setBusy(false)
    }
  }

  // What the lock means, said on hover and, for a role that cannot change it,
  // said why not — a lock that does nothing when clicked should not look like
  // one that is broken.
  const explanation = locked
    ? may
      ? `${tooltipFor(mark)}. Click to remove protection.`
      : `${tooltipFor(mark)}. Removing protection needs someone who approves destroys.`
    : may
      ? 'Not protected: a destroy runs on one person’s say-so. Click to protect it.'
      : 'Not protected: a destroy runs on one person’s say-so.'

  return (
    <span className="lock-control">
      {may ? (
        <button
          type="button"
          className={locked ? 'lock lock-button lock-on' : 'lock lock-button lock-off'}
          title={explanation}
          disabled={busy}
          onClick={() => {
            if (locked) setConfirming(true)
            else void change(true)
          }}
        >
          <LockIcon locked={locked} />
          {word}
        </button>
      ) : (
        <span
          className={locked ? 'lock lock-static lock-on' : 'lock lock-static lock-off'}
          title={explanation}
        >
          <LockIcon locked={locked} />
          {word}
        </span>
      )}
      {problem === undefined ? null : <span className="failure">{problem}</span>}
      {confirming && mark !== undefined ? (
        <Modal
          title={`Remove protection from ${deployment.deploymentId}?`}
          onCancel={() => setConfirming(false)}
        >
          <p>
            Protected by {mark.by}. Without it, a destroy of this instance runs on one
            person&rsquo;s say-so.
          </p>
          <p className="muted">
            You are still treated as protected against for {String(APPROVAL_WINDOW_DAYS)} days: a
            destroy you start yourself in that time waits for a second approver, so removing the
            mark and destroying alone is not one person&rsquo;s act. Anybody else&rsquo;s destroy no
            longer waits.
          </p>
          <div className="actions">
            <button type="button" className="ghost" onClick={() => setConfirming(false)}>
              Keep protection
            </button>
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() => {
                setConfirming(false)
                void change(false)
              }}
            >
              Remove protection
            </button>
          </div>
        </Modal>
      ) : null}
    </span>
  )
}
